import React, { ReactNode, useRef, FC } from 'react';
import { CheckPicker, DateRangePicker, SelectPicker } from 'rsuite';
import { endOfDay, isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import './DisputesFilterSection.scss';
import clsx from 'clsx';
import { SearchInputSmall } from '../../common/SearchInputSmall';
import { useDisputesContext } from '../../../store/disputes/disputes-context';
import { getFormattedDate, makeSearchParams } from '../../../utils/utilities';
import { FilterComponent } from '../../FilterComponent';
import { RenderExtraFooter } from '../../filter/RenderExtraFooter';
import { timeZonesList } from '../../profileSettings/ProfileSettingsPreferences';
import { ButtonComponent } from '../../ButtonComponent';
import { isEqual } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useMerchants } from '../../../store/merchnts/merchants-context';

enum DISPUTES_DATE_FILTERS {
	CREATED = 'created',
	AUTHORIZATION = 'authorization',
	REQUEST = 'request',
}
export interface IDisputesFilters {
	query?: string,
	pageNumber?: string,
	pageSize?: string,
	createdFrom?: Date | string,
	createdTo?: Date | string,
	from?: Date | string,
	to?: Date | string,
	requestFrom?: Date | string,
	requestTo?: Date | string,
	timezone?: string,
	cardType?: string[],
	providerName?: string[],
	stage?: string[],
	isWebhook?: string,
	isFileImported?: string,
	isManualInserted?: string,
	isMatch?: string,
	mid?: string,
}

const disputeTypes = [
	{
		label: 'RDR',
		value: 'RDR',
	},
	{
		label: 'CHARGEBACK',
		value: 'CHARGEBACK',
	},
	{
		label: 'DISPUTE',
		value: 'DISPUTE',
	},
	{
		label: 'PRE-DISPUTE',
		value: 'PRE-DISPUTE',
	},
	{
		label: 'EARLY_WARNING',
		value: 'EARLY_WARNING',
	},
	{
		label: 'REVERSED',
		value: 'REVERSED',
	},
];

const webhookProps = [
	{
		label: 'true',
		value: 'true',
	},
	{
		label: 'false',
		value: 'false',
	},
];

interface Props {
	from: Date | string;
	setFrom: (value: Date | string) => void;
	to: Date | string;
	setTo: (value: Date | string) => void;
	createdFrom: Date | string;
	setCreatedFrom: (value: Date | string) => void;
	createdTo: Date | string;
	setCreatedTo: (value: Date | string) => void;
	requestFrom: Date | string;
	setRequestFrom: (value: Date | string) => void;
	requestTo: Date | string;
	setRequestTo: (value: Date | string) => void;
	timezone: string;
	setTimezone: (value: string) => void;
	query: string;
	setQuery: (value: string) => void;
	providerName: string[];
	setProviderName: (value: string[]) => void;
	cardType: string[];
	setCardType: (value: string[]) => void;
	stage: string[];
	setStage: (value: string[]) => void;
	isWebhook: string;
	setIsWebhook: (value: string) => void;
	isFileImported: string;
	setIsFileImported: (value: string) => void;
	isManualInserted: string;
	setIsManualInserted: (value: string) => void;
	isDisputesDataLoading: boolean;
	pageSize: string;
	pageNumber: string;
	appliedFiltersState: IDisputesFilters;
	setAppliedFiltersState: (value: IDisputesFilters) => void;

}
export const DisputesFilterSection: FC<Props> = (props) => {

	const {
		from, setFrom, to, setTo, createdFrom, setCreatedFrom, createdTo, setCreatedTo,
		requestFrom, setRequestFrom, requestTo, setRequestTo, timezone, setTimezone, query, setQuery,
		providerName, setProviderName, cardType, setCardType, stage, setStage,
		isWebhook, setIsWebhook, isFileImported, setIsFileImported, isManualInserted, setIsManualInserted,
		isDisputesDataLoading, pageSize, pageNumber, appliedFiltersState, setAppliedFiltersState
	} = props;

	const { t } = useTranslation();
	const { pathname } = useLocation();
	const navigate = useNavigate();

	const { merchantState: { chosenMerchant }} = useMerchants();

	const picker = useRef(null);
	const cardRef = useRef(null);
	const typesRef = useRef(null);

	const { disputesContextState } = useDisputesContext();
	const { managementSystem, cardBrands } = disputesContextState;

	const localFilterState: IDisputesFilters = {
		query,
		createdFrom,
		createdTo,
		from,
		to,
		requestFrom,
		requestTo,
		timezone,
		cardType,
		providerName,
		stage,
		isWebhook,
		isFileImported,
		isManualInserted,
		pageNumber,
		pageSize
	};

	const allValue = (managementSystem || [])?.filter(el => el !== null)?.map((value) => ({ label: value, value: value })).map(item => item.value);
	const allCards = (cardBrands || [])?.filter(el => el !== null)?.map((value) => ({ label: value, value: value })).map(item => item.value);
	const allTypes = disputeTypes?.map(item => item.value);

	const handleCheckAll = (value, checked): void => {
		const selectedValues = checked ? allValue : [];
		setProviderName(selectedValues);
	};

	const checkAllCards = (value, checked): void => {
		const selectedValues = checked ? allCards : [];
		setCardType(selectedValues);
	};

	const checkAllTypes = (value, checked): void => {
		const selectedValues = checked ? allTypes : [];
		setStage(selectedValues);
	};

	const isSelectedFilters = createdFrom || from || requestFrom || query || isWebhook
		|| isManualInserted || isFileImported || timezone !== 'UTC'
		|| cardType.length || stage.length || providerName.length;

	const handleCleanFilter = (): void => {
		if (isSelectedFilters) {
			setQuery('');
			setProviderName([]);
			setCardType([]);
			setStage([]);
			setFrom(null);
			setTo(null);
			setCreatedFrom(null);
			setCreatedTo(null);
			setRequestFrom(null);
			setRequestTo(null);
			setTimezone('UTC');
			setIsWebhook('');
			setIsFileImported('');
			setIsManualInserted('');
		}
	};

	const handleSearch = async (): Promise<void> => {
		const refreshedFilters = {
			...localFilterState,
			pageNumber: '1',
			mid: chosenMerchant,
		};

		setAppliedFiltersState(refreshedFilters);
		navigate(`${pathname}?${makeSearchParams(refreshedFilters)}`);
	};

	const setDateFilters = (value: Date[], filterType: DISPUTES_DATE_FILTERS): void => {
		const dateFrom = value?.[0] ? getFormattedDate(value[0], 'yyyy-MM-dd HH:mm:ss') : null;
		const dateTo = value?.[1] ? getFormattedDate(value[1], 'yyyy-MM-dd HH:mm:ss') : null;

		switch (filterType) {
			case DISPUTES_DATE_FILTERS.CREATED:
				setCreatedFrom(dateFrom);
				setCreatedTo(dateTo);
				break;
			case DISPUTES_DATE_FILTERS.AUTHORIZATION:
				setFrom(dateFrom);
				setTo(dateTo);
				break;
			case DISPUTES_DATE_FILTERS.REQUEST:
				setRequestFrom(dateFrom);
				setRequestTo(dateTo);
				break;
			default:
				break;
		}
	};

	return (
		<div className="card bg-transparent fc-disputes-filter" data-cy="dipute-filters-block">
			<div className="card-body py-0 px-0">
				<div className="d-flex align-items-center block-width application-selector mb-5">
					<SearchInputSmall
						placeholder={t('DISPUTES.SEARCH_PLACEHOLDER')}
						searchValue={query}
						setSearchValue={setQuery}
						cypressAttribute="disputes-search-input"
					/>


					<ButtonComponent
						additionalClass="btn-sm ms-10 w-100px d-none d-sm-block"
						onButtonClick={handleSearch}
						isDisabled={isDisputesDataLoading || isEqual(localFilterState, appliedFiltersState)}
						loading={isDisputesDataLoading}
						cypressAttribute="disputes-search-button"
					>
						<>
							{
								isEqual(localFilterState, appliedFiltersState)
									? 'Search'
									: (
										<div className="d-flex align-items-center justify-content-center">
											<FontAwesomeIcon icon={faTriangleExclamation} />
											<span className="ms-2 fw-bolder">Refresh</span>
										</div>
									)
							}
						</>
					</ButtonComponent>
				</div>

				<div className="d-flex align-items-center  fc-filter-line">
					<FilterComponent value={createdFrom} label="Created" labelPlacement="integrated">
						<div className={clsx('fc-datepicker fc-datepicker-request fc-range-datepicker', createdFrom && 'fc-added-icon')}>
							<DateRangePicker
								placeholder="Created"
								placement="auto"
								defaultCalendarValue={[
									new Date(getFormattedDate(new Date().setHours(0, 0, 0), 'yyyy-MM-dd HH:mm:ss')),
									new Date(getFormattedDate(new Date().setHours(23, 59, 59), 'yyyy-MM-dd HH:mm:ss'))
								]}
								onChange={(value): void => setDateFilters(value, DISPUTES_DATE_FILTERS.CREATED)}
								onClean={(): void => setDateFilters([null, null], DISPUTES_DATE_FILTERS.CREATED)}
								shouldDisableDate={(date): boolean => isAfter(date, endOfDay(new Date()))}
								value={!createdFrom || !createdTo ? null : [new Date(createdFrom), new Date(createdTo)]}
								format="yyyy-MM-dd HH:mm:ss"
								data-cy="date-picker-created"
							/>
						</div>
					</FilterComponent>

					<FilterComponent value={from} label="Authorization" labelPlacement="integrated">
						<div className={clsx('fc-datepicker fc-range-datepicker', from && 'fc-added-icon')}>
							<DateRangePicker
								placeholder="Authorization"
								placement="auto"
								defaultCalendarValue={[
									new Date(getFormattedDate(new Date().setHours(0, 0, 0), 'yyyy-MM-dd HH:mm:ss')),
									new Date(getFormattedDate(new Date().setHours(23, 59, 59), 'yyyy-MM-dd HH:mm:ss'))
								]}
								onChange={(value): void => setDateFilters(value, DISPUTES_DATE_FILTERS.AUTHORIZATION)}
								onClean={(): void => setDateFilters([null, null], DISPUTES_DATE_FILTERS.AUTHORIZATION)}
								shouldDisableDate={(date): boolean => isAfter(date, endOfDay(new Date()))}
								value={!from || !to ? null : [new Date(from), new Date(to)]}
								format="yyyy-MM-dd HH:mm:ss"
								data-cy="date-picker-authorization"
							/>
						</div>
					</FilterComponent>

					<FilterComponent value={requestFrom} label="Request" labelPlacement="integrated">
						<div className={clsx('fc-datepicker fc-range-datepicker', requestFrom && 'fc-added-icon')}>
							<DateRangePicker
								placeholder="Request"
								placement="auto"
								defaultCalendarValue={[
									new Date(getFormattedDate(new Date().setHours(0, 0, 0), 'yyyy-MM-dd HH:mm:ss')),
									new Date(getFormattedDate(new Date().setHours(23, 59, 59), 'yyyy-MM-dd HH:mm:ss'))
								]}
								onChange={(value): void => setDateFilters(value, DISPUTES_DATE_FILTERS.REQUEST)}
								onClean={(): void => setDateFilters([null, null], DISPUTES_DATE_FILTERS.REQUEST)}
								shouldDisableDate={(date): boolean => isAfter(date, endOfDay(new Date()))}
								value={!requestFrom || !requestTo ? null : [new Date(requestFrom), new Date(requestTo)]}
								format="yyyy-MM-dd HH:mm:ss"
								data-cy="date-picker-request"
							/>
						</div>
					</FilterComponent>

					<FilterComponent value={timezone} label="Timezone" labelPlacement="integrated">
						<SelectPicker
							data={timeZonesList}
							appearance="subtle"
							placement="bottomEnd"
							placeholder='Time Zone'
							onSelect={(timezone): void => setTimezone(timezone)}
							className="fc-selector timezone-selector"
							onClean={(): void => setTimezone('UTC')}
							cleanable={false}
							defaultValue={timezone}
							value={timezone}
							data-cy="timezone-selector"
						/>
					</FilterComponent>

					<FilterComponent value={cardType.length} label="Card" labelPlacement="integrated">
						<CheckPicker
							data={(cardBrands || [])?.filter(el => el !== null)?.map((cardBrand) => ({ label: cardBrand, value: cardBrand }))}
							ref={cardRef}
							searchable={false}
							value={cardType}
							onChange={(value): void => setCardType(value)}
							onClean={(): void => setCardType([])}
							placeholder='Card'
							className={clsx('fc-selector picker-selector', !cardType.length && 'fc-added-icon')}
							appearance="subtle"
							placement="bottomEnd"
							renderExtraFooter={(): ReactNode => (
								<RenderExtraFooter
									statusFilter={cardType}
									allValue={allCards}
									handleCheckAll={checkAllCards}
									handleSave={(): void => cardRef.current?.close()}
								/>
							)}
							data-cy="card-selector"
						/>
					</FilterComponent>

					<FilterComponent value={providerName.length} label="Source" labelPlacement="integrated">
						<CheckPicker
							data={(managementSystem || [])?.filter(el => el !== null)?.map((value) => ({ label: value, value: value }))}
							ref={picker}
							searchable={false}
							value={providerName}
							onChange={(value): void => setProviderName(value)}
							onClean={(): void => setProviderName([])}
							placeholder='Source'
							className={clsx('fc-selector picker-selector', !providerName.length && 'fc-added-icon')}
							appearance="subtle"
							placement="bottomEnd"
							renderExtraFooter={(): ReactNode => (
								<RenderExtraFooter
									statusFilter={providerName}
									allValue={allValue}
									handleCheckAll={handleCheckAll}
									handleSave={(): void => picker.current?.close()}
								/>
							)}
							data-cy="source-selector"
						/>
					</FilterComponent>

					<FilterComponent value={stage.length} label="Types" labelPlacement="integrated">
						<CheckPicker
							data={disputeTypes}
							ref={typesRef}
							searchable={false}
							value={stage}
							onChange={(value): void => setStage(value)}
							onClean={(): void => setStage([])}
							placeholder='Types'
							className={clsx('fc-selector picker-selector', !stage.length && 'fc-added-icon')}
							appearance="subtle"
							placement="bottomEnd"
							renderExtraFooter={(): ReactNode => (
								<RenderExtraFooter
									statusFilter={stage}
									allValue={allTypes}
									handleCheckAll={checkAllTypes}
									handleSave={(): void => typesRef.current?.close()}
								/>
							)}
							data-cy="types-selector"
						/>
					</FilterComponent>

					<FilterComponent value={isWebhook} label="Is webhook" labelPlacement="integrated">
						<SelectPicker
							data={webhookProps}
							placeholder='Is webhook'
							onSelect={(selectorValue): void => setIsWebhook(selectorValue)}
							onClean={(): void => setIsWebhook('')}
							value={isWebhook}
							searchable={false}
							defaultValue=''
							className={clsx('fc-selector timezone-selector', isWebhook ? 'fc-selector-cleanable' : 'fc-added-icon')}
							appearance="subtle"
							placement="bottomEnd"
							data-cy="is-webhook-selector"
						/>
					</FilterComponent>

					<FilterComponent value={isFileImported} label="Is file imported" labelPlacement="integrated">
						<SelectPicker
							data={webhookProps}
							placeholder='Is File Imported'
							onSelect={(selectorValue): void => setIsFileImported(selectorValue)}
							onClean={(): void => setIsFileImported('')}
							value={isFileImported}
							searchable={false}
							defaultValue=''
							className={clsx('fc-selector timezone-selector', isFileImported ? 'fc-selector-cleanable' : 'fc-added-icon')}
							appearance="subtle"
							placement="bottomEnd"
							data-cy="is-file-imported-selector"
						/>
					</FilterComponent>

					<FilterComponent value={isManualInserted} label="Is manual inserted" labelPlacement="integrated">
						<SelectPicker
							data={webhookProps}
							placeholder='Is Manual Inserted'
							onSelect={(selectorValue): void => setIsManualInserted(selectorValue)}
							onClean={(): void => setIsManualInserted('')}
							value={isManualInserted}
							searchable={false}
							defaultValue=''
							className={clsx('fc-selector timezone-selector', isManualInserted ? 'fc-selector-cleanable' : 'fc-added-icon')}
							appearance="subtle"
							placement="bottomEnd"
							data-cy="is-manual-inserted-selector"
						/>
					</FilterComponent>

					{
						!!isSelectedFilters && (
							<div
								className="text-danger text-decoration-underline cursor-pointer"
								onClick={handleCleanFilter}
								data-cy="dispute-clear-filters-button"
							>
								Clear filters
							</div>
						)
					}
				</div>
			</div>
		</div>
	);
};
